import { useMemo, useState } from 'react';
import { Activity } from 'lucide-react';
import {
  buildAssessmentLongitudinalSeries,
  formatLongitudinalDate,
  formatLongitudinalNumber,
  type AssessmentLongitudinalSeries,
} from '../reports/assessmentLongitudinalDomain';
import type { AssessmentReportSourceSnapshotV2 } from '../reports/assessmentReportDomain';

export interface AssessmentLongitudinalPanelProps {
  sources: readonly AssessmentReportSourceSnapshotV2[];
  showCharts?: boolean;
  onShowChartsChange?: (showCharts: boolean) => void;
}

const signed = (value: number): string => `${value > 0 ? '+' : value < 0 ? '−' : ''}${formatLongitudinalNumber(Math.abs(value))}`;

const SeriesChart = ({ series }: { series: AssessmentLongitudinalSeries }) => {
  const width = 760;
  const height = 205;
  const left = 67;
  const right = width - 35;
  const top = 18;
  const bottom = height - 44;
  const firstTime = series.points[0].timestamp;
  const lastTime = series.points[series.points.length - 1].timestamp;
  const values = series.points.flatMap((point) => point.referenceValue != null
    ? [point.value, point.referenceValue] : [point.value]);
  const minimum = Math.min(...values);
  const maximum = Math.max(...values);
  const padding = Math.max((maximum - minimum) * 0.15, Math.abs(maximum) * 0.05, 0.1);
  const lower = Math.max(0, minimum - padding);
  const upper = maximum + padding;
  const x = (timestamp: number): number => lastTime === firstTime ? (left + right) / 2
    : left + (timestamp - firstTime) / (lastTime - firstTime) * (right - left);
  const y = (value: number): number => bottom - (value - lower) / (upper - lower) * (bottom - top);
  const observedPoints = series.points.map((point) => `${x(point.timestamp)},${y(point.value)}`).join(' ');
  const referencePoints = series.points.filter((point) => point.referenceValue != null)
    .map((point) => `${x(point.timestamp)},${y(point.referenceValue!)}`).join(' ');
  const hasReference = series.points.some((point) => point.referenceValue != null);
  const labels = series.points.filter((_, index) => series.points.length <= 4 || index === 0
    || index === series.points.length - 1 || index === Math.floor(series.points.length / 2));

  return (
    <div className="mt-4 overflow-x-auto rounded-xl border border-slate-100 bg-slate-50/50 px-2 pt-2">
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full min-w-[420px]" role="img"
        aria-label={`${series.testLabel}, ${series.measureLabel}${series.seriesLabel ? `, ${series.seriesLabel}` : ''}; medidas ao longo do tempo em ${series.unit}`}>
        <title>{`${series.measureLabel} (${series.unit})`}</title>
        {[0, 0.5, 1].map((fraction) => {
          const value = lower + (upper - lower) * fraction;
          return <g key={fraction}>
            <line x1={left} x2={right} y1={y(value)} y2={y(value)} stroke="#e2e8f0" strokeWidth="1" />
            <text x={left - 10} y={y(value) + 4} textAnchor="end" fontSize="11" fill="#64748b">{formatLongitudinalNumber(value, 1)}</text>
          </g>;
        })}
        {series.comparable ? <polyline points={observedPoints} fill="none" stroke="#0f766e" strokeWidth="2" /> : null}
        {series.comparable && hasReference ? <polyline points={referencePoints} fill="none" stroke="#94a3b8" strokeWidth="1.5" strokeDasharray="5 4" /> : null}
        {series.points.map((point) => <g key={point.resultId}>
          <circle cx={x(point.timestamp)} cy={y(point.value)} r="4" fill="#0f766e">
            <title>{`${formatLongitudinalDate(point.assessmentDate)}: ${formatLongitudinalNumber(point.value)} ${series.unit}`}</title>
          </circle>
          {point.referenceValue != null ? <circle cx={x(point.timestamp)} cy={y(point.referenceValue)} r="3" fill="#94a3b8" /> : null}
        </g>)}
        {labels.map((point) => <text key={point.resultId} x={x(point.timestamp)} y={bottom + 22}
          textAnchor="middle" fontSize="11" fill="#64748b">{formatLongitudinalDate(point.assessmentDate)}</text>)}
        <text x={left} y={height - 5} fontSize="10" fill="#64748b">{series.unit}</text>
      </svg>
      <div className="flex flex-wrap gap-4 px-4 pb-3 text-xs text-slate-600">
        <span className="inline-flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-teal-700" />Valor medido</span>
        {hasReference ? <span className="inline-flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-slate-400" />Referência registrada na data</span> : null}
      </div>
    </div>
  );
};

export default function AssessmentLongitudinalPanel({ sources, showCharts: controlledShowCharts, onShowChartsChange }: AssessmentLongitudinalPanelProps) {
  const [localShowCharts, setLocalShowCharts] = useState(true);
  const showCharts = controlledShowCharts ?? localShowCharts;
  const setShowCharts = (value: boolean) => {
    if (onShowChartsChange) onShowChartsChange(value);
    else setLocalShowCharts(value);
  };
  const series = useMemo(() => buildAssessmentLongitudinalSeries(sources), [sources]);
  const descriptiveSources = sources.filter((source) => source.snapshot.sourceStatus === 'completed'
    && source.snapshot.testKey !== 'INITIAL_ASSESSMENT'
    && source.validity.status !== 'invalid'
    && !source.snapshot.longitudinalMeasures?.length);

  return (
    <section className="space-y-4" aria-label="Resultados longitudinais">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-semibold text-slate-900">Evolução longitudinal das medidas</h3>
          <p className="mt-1 text-sm text-slate-600">Cada série preserva a data, a condição de aplicação e a referência da avaliação de origem.</p>
        </div>
        {series.some((item) => item.points.length > 1) && (controlledShowCharts === undefined || onShowChartsChange) ? <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={showCharts} onChange={(event) => setShowCharts(event.target.checked)}
            className="rounded border-slate-300 text-primary-600 focus:ring-primary-500" /> Mostrar gráficos
        </label> : null}
      </div>

      {!series.length && !descriptiveSources.length ? <div className="rounded-xl border border-dashed border-slate-200 p-5 text-sm text-slate-500">
        Selecione os resultados e as datas que deseja reunir. Medidas únicas também podem integrar o relatório.
      </div> : null}

      {series.map((item) => {
        const hasReference = item.points.some((point) => point.referenceValue != null);
        const hasPercentPredicted = item.points.some((point) => point.percentPredicted != null);
        return <article key={item.key} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
          <div className="flex items-start gap-3">
            <Activity className="mt-0.5 h-5 w-5 shrink-0 text-primary-700" />
            <div>
              <h4 className="font-semibold text-slate-900">{item.testLabel}</h4>
              <p className="mt-1 text-sm text-slate-600">{item.measureLabel}{item.seriesLabel ? ` · ${item.seriesLabel}` : ''}</p>
            </div>
          </div>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead><tr className="border-b border-slate-200 text-xs text-slate-500">
                <th className="px-2 py-2 font-semibold">Data</th>
                <th className="px-2 py-2 text-right font-semibold">Resultado ({item.unit})</th>
                {hasReference ? <th className="px-2 py-2 text-right font-semibold">Valor previsto ({item.unit})</th> : null}
                {hasPercentPredicted ? <th className="px-2 py-2 text-right font-semibold">% do previsto</th> : null}
              </tr></thead>
              <tbody>{item.points.map((point) => <tr key={point.resultId} className="border-b border-slate-100 last:border-0">
                <td className="whitespace-nowrap px-2 py-3 text-slate-700">{formatLongitudinalDate(point.assessmentDate)}</td>
                <td className="px-2 py-3 text-right font-semibold tabular-nums text-slate-900">{formatLongitudinalNumber(point.value)}</td>
                {hasReference ? <td className="px-2 py-3 text-right tabular-nums text-slate-600">{point.referenceValue != null ? formatLongitudinalNumber(point.referenceValue) : 'Não registrado'}</td> : null}
                {hasPercentPredicted ? <td className="px-2 py-3 text-right tabular-nums text-slate-600">{point.percentPredicted != null ? `${formatLongitudinalNumber(point.percentPredicted, 1)}%` : 'Não registrado'}</td> : null}
              </tr>)}</tbody>
            </table>
          </div>
          {item.delta != null ? <p className="mt-3 text-sm text-slate-700">
            Variação entre o primeiro e o último registro: <strong>{signed(item.delta)} {item.unit}</strong>
            {item.deltaPercent != null ? ` (${signed(item.deltaPercent)}%)` : ''}.
          </p> : null}
          {item.comparisonNote ? <p className="mt-3 text-xs text-slate-500">{item.comparisonNote}</p> : null}
          {hasReference ? <div className="mt-3 space-y-1 text-xs text-slate-500">{item.points.filter((point) => point.referenceLabel)
            .map((point) => <p key={point.resultId}>{formatLongitudinalDate(point.assessmentDate)}: {point.referenceLabel}</p>)}</div> : null}
          {showCharts && item.points.length > 1 ? <SeriesChart series={item} /> : null}
        </article>;
      })}

      {descriptiveSources.length ? <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
        <h4 className="font-semibold text-slate-900">Demais resultados selecionados</h4>
        <div className="mt-3 space-y-3">{descriptiveSources.map((source) => <div key={source.resultId} className="border-b border-slate-100 pb-3 last:border-0 last:pb-0">
          <p className="text-sm font-medium text-slate-800">{source.snapshot.testLabel} · {formatLongitudinalDate(source.snapshot.assessmentDate)}</p>
          <p className="mt-1 text-sm text-slate-600">{source.snapshot.primaryMeasure.formattedValue}</p>
        </div>)}</div>
      </div> : null}
      {series.length ? <p className="text-xs leading-5 text-slate-500">As diferenças descrevem mudanças observadas. A importância clínica, a evolução das metas e o diagnóstico fisioterapêutico são registrados na análise profissional.</p> : null}
    </section>
  );
}
